import { SingleValue } from "react-select";
import {
  FieldOption,
  Attribute,
  AttributeGroup,
  AttributeSet,
  AttributeSetGroup,
  AttributeOptionSet,
  IsUsedForDisablingGroup,
  AttributeSetGroupElement,
  BoGroupByAll,
  GroupByAll,
  LabelOptions,
  Option,
  GroupParentAttributeOptionSet,
  GroupAttributeCodeOptionDependency,
  GlobalGroupProducts,
  OptionDependency,
  OptionDependencyKey,
  Product,
  DataToFetchProducts,
  GroupAttributeCodes,
  BodyAttributes,
  FormFields,
  GroupParentAttributeOptionSetValue,
  GroupAttributeCodeSetSource,
  AttributeCodeSource,
  GroupAttributeCodeSetSourceValue,
  FilteredProduct,
  ParentAttributeOptionSet,
} from "@/components/configurator/global-option/types";
import { findRuleBlockedGroupIds } from "@/rules/apply-rules";
import type { GlobalOptions } from "@/utils/form-data-manager";
import getGlobalOptionsPreferences from "@/utils/magento/quotes/get-global-options-preferences";
import setGlobalOptionsPreferences from "@/utils/magento/set-global-options-preferences";
const nullAttributeOptionSet: AttributeOptionSet = {
  attributeCode: "",
  defaultOption: {
    label: "",
    value: "",
    isDisabled: false,
  },
  selectedOption: {
    label: "",
    value: "",
    isDisabled: false,
  },
  labelOptions: {
    label: "",
    options: [
      {
        label: "",
        value: "",
        isDisabled: false,
      },
    ],
  },
  isDisabled: false,
  isGrayOut: false,
  dukeGeneralRowsContainer: null,
};

export const getGroupAttributeCodeSetSource = (
  groupAttributesCodesSet: GroupAttributeCodes[],
  formFields: FormFields,
): GroupAttributeCodeSetSource => {
  const groupAttributeCodeSetSource: GroupAttributeCodeSetSource = new Map<
    string,
    GroupAttributeCodeSetSourceValue
  >();

  groupAttributesCodesSet.forEach((groupAttributeCodes) => {
    const groupParentOptions = formFields.get(
      groupAttributeCodes.attribute_group,
    );

    const attributeCodeSource: GroupAttributeCodeSetSourceValue = {
      groupName: groupParentOptions?.groupName || "",
      attributeCodeSet: [],
      attributeSetId: "",
    };

    groupAttributeCodes.bodyAttributes?.forEach((bodyAttribute) => {
      let attributeCodeSet: AttributeCodeSource = {
        attributeCode: bodyAttribute.attribute_code,
        label: bodyAttribute.label,
        source: "none",
        value: "",
      };

      if (groupParentOptions) {
        for (let [key, value] of groupParentOptions.parents) {
          value.forEach((attributeSet) => {
            if (bodyAttribute.attribute_code === attributeSet.attribute_code) {
              attributeCodeSet.source = "global";
            }
          });
          attributeCodeSource.attributeCodeSet.push(attributeCodeSet);
        }
      }
    });

    groupAttributeCodeSetSource.set(
      groupAttributeCodes.attribute_group,
      attributeCodeSource,
    );
  });

  return groupAttributeCodeSetSource;
};

export function createGroupByAll(
  attributeSet: Attribute[],
  attributeGroups: AttributeGroup[],
): FormFields {
  const groupByAllSet: FormFields = new Map();

  const groupsIdSet = Array.from(
    new Set(attributeSet.map((attr) => attr.group_id)),
  );

  groupsIdSet.forEach((groupId) => {
    const groupAttributeSet = attributeSet.filter(
      (attribute) => attribute.group_id === groupId,
    );

    const parentSet = Array.from(
      new Set(groupAttributeSet.map((attr) => attr.parent)),
    );

    const parents: Map<string, Attribute[]> = new Map();

    parentSet.forEach((parent) => {
      parents.set(
        parent === "" ? "default" : parent,
        groupAttributeSet.filter((attribute) => attribute.parent === parent),
      );
    });

    const groupName = attributeSet.find(
      (attribute) => attribute.group_id === groupId,
    )?.group;

    const isProduct =
      attributeGroups?.find(
        (attribute) => attribute.attribute_group === groupId,
      )?.fetch_product ?? "1";

    const selectionType =
      attributeGroups?.find(
        (attribute) => attribute.attribute_group === groupId,
      )?.selection_type ?? "multi";

    let groupByAll: GroupByAll = {
      groupId: groupId,
      groupName: "",
      isProduct: false,
      isCollapsed: false,
      isGrayOut: false,
      isDisabled: false,
      selectionType: "",
      parents: new Map(),
    };

    if (groupName && isProduct && selectionType) {
      groupByAll = {
        groupId: groupId,
        groupName: groupName,
        isProduct: isProduct === "1",
        isCollapsed: false,
        isGrayOut: false,
        isDisabled: false,
        selectionType: selectionType,
        parents: parents,
      };

      groupByAllSet.set(groupId, groupByAll);
    }
  });

  return groupByAllSet;
}

const intersectSets = (setA: Set<number>, setB: Set<number>): Set<number> => {
  return new Set([...setA].filter((value) => setB.has(value)));
};

export function updateSelections(
  groupParentAttributeOptionSet: GroupParentAttributeOptionSet,
  selectedOption: SingleValue<Option>,
  value: AttributeOptionSet,
  parent: string,
  groupKey: string,
): GroupParentAttributeOptionSet {
  const parents: Map<string, ParentAttributeOptionSet> | null =
    groupParentAttributeOptionSet.get(groupKey)?.parents ?? null;

  if (!parents) return groupParentAttributeOptionSet;

  const parentMap = new Map(parents);

  let parentSelectedOptions =
    parentMap.get(parent)?.parentSelectedOptions ?? [];

  const attributeOptionToUpdate: AttributeOptionSet =
    groupParentAttributeOptionSet
      .get(groupKey)
      ?.parents.get(parent)
      ?.child?.filter(
        (labelOptions) => labelOptions.attributeCode === value.attributeCode,
      )
      .at(0) ?? nullAttributeOptionSet;

  const nanValue = ["None", "No"];
  let isNanSelected: boolean = false;
  let isNanjustUnselected: boolean = false;

  if (selectedOption?.value === value.selectedOption?.value) {
    attributeOptionToUpdate.selectedOption = {
      label: "",
      value: "",
      isDisabled: false,
    };
    parentSelectedOptions = parentSelectedOptions.filter(
      (item) => item !== selectedOption!.value,
    ); // field dependency logic
    isNanjustUnselected = nanValue.includes(selectedOption?.label ?? "");
  } else if (
    selectedOption &&
    selectedOption?.value !== value.selectedOption?.value
  ) {
    if (value.isMultiselect) {
      parentSelectedOptions = parentSelectedOptions.filter(
        (item) => item !== value.selectedOption?.value,
      );
    }

    isNanSelected = nanValue.includes(selectedOption?.label ?? "");

    attributeOptionToUpdate.selectedOption = selectedOption;
    // field dependency logic
    if (!parentSelectedOptions.includes(selectedOption!.value)) {
      parentSelectedOptions.push(selectedOption!.value);
    }
  }

  // field dependency logic — scoped to the current parent only
  const updatedAttributes =
    parentMap.get(parent)?.child.map((child) => {
      if (child.attributeCode === value.attributeCode) {
        attributeOptionToUpdate.labelOptions.options =
          attributeOptionToUpdate.labelOptions.options.map((option) => {
            option.isDisabled = child.isProduct
              ? !isOptionEnable(
                  parentSelectedOptions,
                  option.enabledWithOptionIds,
                )
              : false;
            return option;
          });
        return attributeOptionToUpdate;
      }
      child.labelOptions.options = child.labelOptions.options.map((option) => {
        option.isDisabled = child.isProduct
          ? !isOptionEnable(parentSelectedOptions, option.enabledWithOptionIds)
          : false;
        return option;
      });
      return child;
    }) || [];

  // Cross-parent dependency: only runs for dependent parents that have isDependentParent: true
  if (isNanSelected && parentMap.get(parent)?.dependentParent.length) {
    parentMap.get(parent)?.dependentParent.forEach((parentKey: string) => {
      let paraentName: string = "";
      groupParentAttributeOptionSet
        .get(groupKey)!
        .parents.forEach((value, key) => {
          if (value.parentAttributeSetKey === parentKey) {
            paraentName = key;
          }
        });
      if (paraentName) {
        const dependentParentData = groupParentAttributeOptionSet
          .get(groupKey)!
          .parents.get(paraentName);
        if (!dependentParentData?.isDependentParent) return;

        groupParentAttributeOptionSet
          .get(groupKey)!
          .parents.get(paraentName)!.child =
          parentMap.get(paraentName)?.child.map((child) => ({
            ...child,
            selectedOption: child.selectedOption?.value
              ? { label: "", value: "", isDisabled: false }
              : child.selectedOption,
            labelOptions: {
              ...child.labelOptions,
              options: child.labelOptions.options.map((option) => ({
                ...option,
                isDisabled: child.isProduct ? true : false,
              })),
            },
          })) || [];

        groupParentAttributeOptionSet
          .get(groupKey)!
          .parents.get(paraentName)!.parentSelectedOptions = [];
      }
    });
  } else if (
    isNanjustUnselected &&
    parentMap.get(parent)?.dependentParent.length
  ) {
    parentMap.get(parent)?.dependentParent.forEach((parentKey: string) => {
      let paraentName: string = "";
      groupParentAttributeOptionSet
        .get(groupKey)!
        .parents.forEach((value, key) => {
          if (value.parentAttributeSetKey === parentKey) {
            paraentName = key;
          }
        });
      if (paraentName) {
        const dependentParentData = groupParentAttributeOptionSet
          .get(groupKey)!
          .parents.get(paraentName);
        if (!dependentParentData?.isDependentParent) return;

        groupParentAttributeOptionSet
          .get(groupKey)!
          .parents.get(paraentName)!.child =
          parentMap.get(paraentName)?.child.map((child) => ({
            ...child,
            labelOptions: {
              ...child.labelOptions,
              options: child.labelOptions.options.map((option) => ({
                ...option,
                isDisabled: false,
              })),
            },
          })) || [];
      }
    });
  }

  groupParentAttributeOptionSet.get(groupKey)!.parents.get(parent)!.child =
    updatedAttributes;

  groupParentAttributeOptionSet
    .get(groupKey)!
    .parents.get(parent)!.parentSelectedOptions = parentSelectedOptions;

  return groupParentAttributeOptionSet;
}

/**
 * Unselects every group the "attribute.options" rules now block after a change in `groupKey`
 * (e.g. a Tray Slide group once the Cutting Board takes its side), as if each selected option
 * were clicked again, so the field dependency logic of updateSelections still runs.
 */
export function clearRuleBlockedGroups(
  groupParentAttributeOptionSet: GroupParentAttributeOptionSet,
  groupKey: string,
): { optionSet: GroupParentAttributeOptionSet; clearedGroupKeys: string[] } {
  // Built here, not with mapDukeActiveStructure: that would import @/utils/helper, which imports
  // this file, and it saves a "default" parent as "" instead of its real key
  const picks: GlobalOptions[] = [...groupParentAttributeOptionSet].flatMap(([key, group]) =>
    [...group.parents].flatMap(([parentName, parent]) =>
      parent.child.map((attribute) => ({
        group_name: group.groupName,
        group_key: key,
        parent_name: parentName,
        key: attribute.attributeCode,
        value: attribute.selectedOption?.value ?? "",
        label: attribute.selectedOption?.label ?? "",
      })),
    ),
  );
  const clearedGroupKeys = findRuleBlockedGroupIds(picks, groupKey, groupParentAttributeOptionSet);

  let optionSet = groupParentAttributeOptionSet;
  clearedGroupKeys.forEach((clearedGroupKey) =>
    optionSet.get(clearedGroupKey)?.parents.forEach((parent, parentName) =>
      [...parent.child].forEach((attribute) => {
        if (!attribute.selectedOption?.value) return;
        optionSet = updateSelections(
          optionSet,
          attribute.selectedOption,
          attribute,
          parentName,
          clearedGroupKey,
        );
      }),
    ),
  );

  return { optionSet, clearedGroupKeys };
}

export const getGroupAttributeCodeOptionDependency = (
  formFields: FormFields,
  globalGroupProducts: GlobalGroupProducts,
): GroupAttributeCodeOptionDependency => {
  const groupAttributeCodeOptionDependency: GroupAttributeCodeOptionDependency =
    new Map();
  Array.from(formFields.keys()).forEach((groupKey) => {
    const globalValue: Map<string, OptionDependency> = new Map();

    const groupParentAttributeOption = formFields.get(groupKey);

    if (!groupParentAttributeOption) return;

    Array.from(groupParentAttributeOption.parents.keys()).forEach(
      (parentKey) => {
        const parentData = groupParentAttributeOption.parents.get(parentKey);

        let optionDependency: OptionDependency = new Map();

        if (!parentData) return;

        if (groupParentAttributeOption.isProduct)
          parentData.forEach((attributeOptionSet) => {
            const attributeCode = attributeOptionSet.attribute_code;

            const labelOptions: FieldOption[] =
              attributeOptionSet.field_options;

            labelOptions.forEach((option) => {
              const optionDependencyKey: OptionDependencyKey = {
                optionLabel: option.label ?? "",
                optionValue: option.value ?? "",
              };

              const optionDependencyValue: Set<number> = getAllEligibleOptions(
                groupKey,
                globalGroupProducts,
                attributeCode,
                option.value ?? "",
                parentData,
              );

              optionDependency.set(optionDependencyKey, optionDependencyValue);
            });

            if (attributeOptionSet.attribute_code == "kickplates_paint_grip") {
              optionDependency = kickplatesPaintGripDependency(
                labelOptions,
                optionDependency,
              );
            }
            if (
              attributeOptionSet.attribute_code == "kickplates_stainless_steel"
            ) {
              optionDependency = kickplatesStainlessSteelDependency(
                labelOptions,
                optionDependency,
              );
            }
          });

        globalValue.set(parentKey, optionDependency);
      },
    );

    groupAttributeCodeOptionDependency.set(
      { groupId: groupKey, groupName: groupParentAttributeOption.groupName },
      globalValue,
    );
  });
  return groupAttributeCodeOptionDependency;
};

const kickplatesPaintGripDependency = (
  labelOptions: FieldOption[],
  optionDependency: OptionDependency,
): OptionDependency => {
  optionDependency.forEach((value, key) => {
    if (key.optionLabel !== "None")
      labelOptions.forEach((option) => {
        value.add(Number(option.value));
      });
    optionDependency.set(key, value);
  });
  return optionDependency;
};
const kickplatesStainlessSteelDependency = (
  labelOptions: FieldOption[],
  optionDependency: OptionDependency,
): OptionDependency => {
  const neglectKeys: string[] = [
    "None",
    "Bright White",
    "Brown Kickplate",
    "Fence Green",
    "Hollyberry Red",
    "Natural Almond",
    "Orange",
    "Racing Red",
    "Semi-Gloss Black",
    "Silver Hammer",
    "Sky Blue",
    "Stone Gray",
    "Textured Black",
  ];

  optionDependency.forEach((value, key) => {
    if (!neglectKeys.includes(key.optionLabel))
      labelOptions.forEach((option) => {
        value.add(Number(option.value));
      });
    optionDependency.set(key, value);
  });
  return optionDependency;
};
const getAllEligibleOptions = (
  groupKey: string,
  globalGroupProducts: GlobalGroupProducts,
  attributeCode: string,
  optionValue: string,
  attribute: Attribute[],
) => {
  const eligibleOptions: Set<number> = new Set();

  const globalGroupProductKeys = Array.from(globalGroupProducts.keys());

  const productSet = globalGroupProducts.get(
    globalGroupProductKeys.filter((key) => key.groupId === groupKey)[0],
  );

  if (!productSet) return eligibleOptions;

  const allOptionValues: string[] = [];

  attribute.forEach((element) => {
    element.field_options.forEach((option) => {
      allOptionValues.push(option.value);
    });
  });

  const eligibleProducts = productSet.filter((product: Product) => {
    if (`${product[attributeCode]}` === optionValue) {
      return true;
    }
  });

  eligibleProducts.forEach((product: Product) => {
    Object.entries(product).forEach(([key, value]) => {
      const strVal = `${value}`;
      allOptionValues.includes(strVal) && eligibleOptions.add(value);
    });
  });

  if (optionValue !== "") {
    eligibleOptions.add(Number(optionValue));
  }

  return eligibleOptions;
};

const getAttributeCodes = (element?: BodyAttributes[]): string[] => {
  if (!element) return [];
  const attributeCode: string[] = [];
  element.forEach((element) => {
    attributeCode.push(element.attribute_code);
  });
  return attributeCode;
};

export const getGlobalGroupProducts = (
  dataToFetchProducts: DataToFetchProducts[],
  fetchedProducts: any,
): GlobalGroupProducts => {
  const globalGroupProducts: GlobalGroupProducts = new Map();
  dataToFetchProducts.forEach((element) => {
    const groupKey = element.groupId;
    const groupName = element.groupName;
    const attributeCodes = element?.attributeCodes || [];
    const optionQueryValue = element.optionQueryValue;
    fetchedProducts.forEach((productSet: any) => {
      if (
        productSet?.length > 0 &&
        String(productSet?.at(0)?.attribute_set) === optionQueryValue &&
        attributeCodes?.length > 0
      ) {
        globalGroupProducts.set(
          {
            groupId: groupKey,
            groupName: groupName,
          },
          productSet,
        );
      }
    });
  });
  return globalGroupProducts;
};

export const updateNonApplianceFields = (globalOptions: any, fields: any) => {
  const nonApplianceFields: { [key: string]: string } = {};
  globalOptions.forEach((obj: any) => {
    if (obj.key === "fheight") {
      fields["fHeight"] = obj.label.substring(0, 2);
    }

    if (obj.key === "fGauge") {
      fields["fGauge"] = obj.label.substring(0, 2);
    }

    fields["fIsDuchessConfigMode"] = "normal";
    if (obj.key === "depth") {
      if (!obj.label.includes("30")) {
        fields["fExpField34"] = "1117.0";
        fields["fCustomManualDesc"] =
          "Depth(in) = " +
          obj.label.substring(0, 3) +
          "\n" +
          (fields["fCustomManualDesc"] ?? "");
      } else {
        fields["fExpField34"] = null;
        //fields["fCustomManualDesc"] = "";
      }
    }
  });

  return fields;
};

const defaultAttributeSetGroupElement: AttributeSetGroupElement = {
  attribute_group: null,
  fetch_product: null,
  selection_type: null,
  tool_tip: null,
};

export const structureData = (
  data: AttributeSet,
  rawAttributeSetGroup: AttributeSetGroup,
): {
  groupByAll: BoGroupByAll;
  usedForDisablingGroup: IsUsedForDisablingGroup[];
} => {
  const isUsedForDisablingGroup: IsUsedForDisablingGroup[] = [];
  const groupedByAll: BoGroupByAll = {};
  data.forEach((attribute: Attribute) => {
    if (attribute?.field_options.length > 0) {
      const group = attribute.group;
      const parent = attribute.parent ? attribute.parent : "parent";

      if (!groupedByAll[group]) {
        const attributeSetGroupElement: AttributeSetGroupElement =
          rawAttributeSetGroup.find(
            (attributeSetGroupElement) =>
              attributeSetGroupElement.attribute_group === attribute.group_id,
          ) || defaultAttributeSetGroupElement;
        groupedByAll[group] = {
          attributeSetGroupElement: attributeSetGroupElement,
        } as BoGroupByAll[string];
      }

      if (attribute.is_used_for_disabling_body) {
        const foundIndex = isUsedForDisablingGroup.findIndex(
          (ele) => ele.groupName === group,
        );
        const option =
          attribute.field_options.find(
            (option) => option.label === attribute.default,
          )?.value ?? "";
        if (foundIndex === -1) {
          isUsedForDisablingGroup.push({
            groupName: group,
            parentName: parent,
            attributes: [
              {
                attribute_code: attribute.attribute_code,
                attribute_value: option ?? "",
                attribute_label: attribute?.default ?? "",
              },
            ],
          });
        } else {
          isUsedForDisablingGroup[foundIndex].attributes.push({
            attribute_code: attribute.attribute_code,
            attribute_value: option ?? "",
            attribute_label: attribute?.default ?? "",
          });
        }
      }

      if (!groupedByAll[group][parent]) {
        groupedByAll[group][parent] = [];
      }

      groupedByAll[group][parent].push(attribute);
    }
  });
  return {
    groupByAll: groupedByAll,
    usedForDisablingGroup: isUsedForDisablingGroup,
  };
};

// new Implementaion
export function getInitialData(data: any): GroupParentAttributeOptionSet {
  let groupParentAttributeOptionSet: GroupParentAttributeOptionSet = new Map();

  data.data.GetStoresConfigurations.group.forEach((group: any) => {
    const groupId = group.group_key.slice(2);
    const groupName = group.group_label;
    const isVisible = group.group_is_visible;
    const isProduct = group.group_is_product ?? false;
    const isTooltipVisible = group.group_is_tooltip_visible ?? false;
    const isChildDependentOnMainBody =
      group.is_child_dependent_on_main_body ?? false;
    const isMiscellaneous = group.is_miscellaneous ?? false;
    const tooltipData = group.tooltip_data ?? {};
    const parents: Map<string, ParentAttributeOptionSet> = new Map();

    group.parent.forEach((parent: any) => {
      const parentLabel = parent.parent_label;
      const groupParentLabel = parent.group_parent_label;
      const parentAttributeSetKey = parent.attribute_set;
      const selectionType = parent.parent_selection_type ? "multi" : "single";
      const isProduct = parent.parent_is_product;
      const isParentVisible = parent.parent_is_visible;
      const dependentParent = parent.dependent_parents;
      const isDependentParent = parent.is_parent_dependent ?? false;
      const filterByGroup = parent.filter_by_group;
      // Names of sub-accessory groups to hide when this parent's body is picked.
      const subAccessoriesHiddenWithThisGroup: string[] = (
        parent.sub_accessories_hidden_with_this_group ?? []
      )
        .map((name: string) => String(name ?? "").trim())
        .filter(Boolean);

      // field dependency logic
      let parentSelectedOptions: string[] = [];

      const parentAttributeOptionSet: ParentAttributeOptionSet = {
        parentAttributeSetKey,
        parentLabel,
        selectionType,
        isProduct,
        isParentVisible,
        isDependentParent,
        parentSelectedOptions,
        dependentParent,
        filterByGroup,
        subAccessoriesHiddenWithThisGroup,
        child: [],
      };

      parents.set(groupParentLabel, parentAttributeOptionSet);

      parent.child.forEach((child: any) => {
        if (child == null) return;
        const dukeGeneralRowsContainer = JSON.parse(
          child.duke_general_rows_container,
        )?.duke_general_rows_container;

        const defaultOptionKey = child.default_option || null;
        const defaultOptionObject =
          Array.isArray(child.options) && child.options.length > 0
            ? child.options.find(
                (option: any) => option.option_key === defaultOptionKey,
              )
            : null;

        const defaultOptionLabel = defaultOptionObject?.option_label || null;
        const enabledWithOptionIds =
          defaultOptionObject?.enabled_with_option_ids?.map(String) || [];

        const attributeCode = child.attribute_code || null;
        const defaultOption: Option = {
          label: defaultOptionLabel ?? "",
          value: defaultOptionKey ?? "",
          isDisabled: false,
          enabledWithOptionIds,
        };

        let selectedOption: Option = {
          label: "",
          value: "",
          isDisabled: true,
        };

        // field dependency logic
        if (parentSelectedOptions.length === 0) {
          if (
            defaultOptionKey &&
            (enabledWithOptionIds.length > 0 || !child.child_is_product)
          ) {
            parentSelectedOptions.push(defaultOptionKey);
            selectedOption = { ...defaultOption };
          }
        } else {
          if (defaultOptionKey && enabledWithOptionIds.length) {
            if (isOptionEnable(parentSelectedOptions, enabledWithOptionIds)) {
              parentSelectedOptions.push(defaultOptionKey);
              selectedOption = { ...defaultOption };
            }
          }
        }

        const disabledOptions = child.child_options_to_disable || [];

        const options: Option[] = child.options
          ? child.options
              .filter((opt: any) => {
                return !disabledOptions.includes(opt.option_key);
              })
              .map((opt: any) => ({
                label: opt.option_label,
                value: opt.option_key,
                enabledWithOptionIds:
                  opt.enabled_with_option_ids?.map((id: number) =>
                    String(id),
                  ) || [],
              }))
          : [];

        const labelOptions: LabelOptions = {
          label: child.store_label ? child.store_label : child.child_label,
          options,
        };

        const attributeOptionSet: AttributeOptionSet = {
          attributeCode,
          selectedOption,
          defaultOption,
          labelOptions,
          isDisabled: false,
          isProduct: child.child_is_product,
          isChildVisible: child.child_is_visible,
          dukeGeneralRowsContainer,
          isMultiselect: child.child_is_multiselect,
        };

        parents.get(groupParentLabel)?.child.push(attributeOptionSet);
      });
      // field dependency logic
      // update the isDisabled
      const updatedAttributes: AttributeOptionSet[] = parents
        .get(groupParentLabel)!
        .child.map((child: AttributeOptionSet) => {
          child.labelOptions.options = child.labelOptions.options.map(
            (option: any) => {
              option.isDisabled = child.isProduct
                ? !isOptionEnable(
                    parentSelectedOptions,
                    option.enabledWithOptionIds,
                  )
                : false;
              return option;
            },
          );
          return child;
        });

      parents.get(groupParentLabel)!.child = updatedAttributes;
      parents.get(groupParentLabel)!.parentSelectedOptions =
        parentSelectedOptions;
    });

    groupParentAttributeOptionSet.set(groupId, {
      groupName,
      groupId,
      isCollapsed: false,
      isVisible,
      isProduct,
      parents,
      isTooltipVisible,
      tooltipData,
      isChildDependentOnMainBody,
      isMiscellaneous,
    });
  });

  return groupParentAttributeOptionSet;
}

export function getDependentParentAttrCodes(
  groupParentAttributeOptionSet: GroupParentAttributeOptionSet,
  groupId: string,
  parent: string,
): string[] {
  const attrCodes: string[] = [];
  const triggeringParent = groupParentAttributeOptionSet
    .get(groupId)
    ?.parents.get(parent);

  if (!triggeringParent?.dependentParent?.length) return attrCodes;

  triggeringParent.dependentParent.forEach((parentKey: string) => {
    groupParentAttributeOptionSet.get(groupId)?.parents.forEach((parentVal) => {
      if (
        parentVal.parentAttributeSetKey === parentKey &&
        parentVal.isDependentParent
      ) {
        parentVal.child.forEach((child) => {
          if (child.attributeCode) attrCodes.push(child.attributeCode);
        });
      }
    });
  });

  return attrCodes;
}

// field dependency logic
export function isOptionEnable(
  selectedOptions: string[],
  OptionsAvailable: any,
) {
  if (OptionsAvailable.length === 0) return false;
  return selectedOptions.every((option) => OptionsAvailable.includes(option));
}

export function setGlobalDependentAttributeSets(
  responseData: any,
  groupAttributeCodeSetSource: GroupAttributeCodeSetSource,
) {
  const globalDependentAttributeSets: string[] = [];
  const groupAttributesCodesSet: GroupAttributeCodes[] =
    responseData?.data?.GetStoresConfigurations?.group;

  groupAttributesCodesSet.forEach((group: any) => {
    group.parent.forEach((parent: any) => {
      if (parent.is_global_to_global_dependent) {
        globalDependentAttributeSets.push(
          parent.group_parent_label.replace("Default", "").trim(),
        );
      }

      //To Do More Optimize
      if (parent.filter_by_group.length) {
        let dependentOnName: any = [];
        parent.filter_by_group.forEach((groupkey: any) => {
          groupAttributeCodeSetSource.forEach((value: any, key: any) => {
            if (key === groupkey) {
              dependentOnName.push(value.groupName);
            }
          });
        });
        groupAttributeCodeSetSource.forEach((value: any) => {
          if (
            value.groupName ===
            parent.group_parent_label.replace("Default", "").trim()
          ) {
            value.dependentOn = dependentOnName;
          }
        });
      }
      groupAttributeCodeSetSource.forEach((value: any) => {
        if (
          value.groupName ===
          parent.group_parent_label.replace("Default", "").trim()
        ) {
          value.attributeSetId = parent.attribute_set;
        }
      });
    });
  });

  globalDependentAttributeSets.forEach((groupName: string) => {
    groupAttributeCodeSetSource.forEach((value: any) => {
      if (value.groupName === groupName) {
        value.attributeCodeSet.forEach((attributeCodeSet: any) => {
          attributeCodeSet.source = "global";
        });
      }
    });
  });
  return groupAttributeCodeSetSource;
}

export const getSelectedOptions = async (
  sales_Rep_Id: any,
  activeSession: any,
) => {
  try {
    if (!sales_Rep_Id) {
      return null;
    }

    const getSelectedOptions = await getGlobalOptionsPreferences(
      sales_Rep_Id,
      activeSession,
      "globalOptionsPreferences",
    );
    // Check if response has data
    if (!getSelectedOptions?.data) {
      return null;
    }

    // Extract configuration value from response
    const configurationValue =
      getSelectedOptions.data?.getUserConfiguration?.configurations?.[0]
        ?.configuration_value;

    if (!configurationValue) {
      return null;
    }

    try {
      const selections = JSON.parse(configurationValue);
      return selections;
    } catch (parseError) {
      console.error("Error parsing configuration value:", parseError);
      return null;
    }
  } catch (error) {
    console.error("Error fetching selected options:", error);
    return null;
  }
};

export const setSelectedOptions = async (
  sales_Rep_Id: any,
  activeSession: any,
  selectedOptions: string[],
) => {
  try {
    if (!sales_Rep_Id) {
      throw new Error("No user ID provided for setting selected options");
    }

    if (!Array.isArray(selectedOptions)) {
      throw new Error("Selected options must be an array");
    }

    const result = await setGlobalOptionsPreferences(
      sales_Rep_Id,
      activeSession,
      "globalOptionsPreferences",
      JSON.stringify(selectedOptions),
    );

    return result;
  } catch (error) {
    console.error("Error setting selected options:", error);
    throw error;
  }
};

export const getSelectedOptionsFromDefaults = (
  defaultOptions: GroupParentAttributeOptionSet,
  userSelectedOptions: string[] | null,
): GroupParentAttributeOptionSet => {
  const selectedMap = new Map<string, any>();

  if (
    userSelectedOptions &&
    Array.isArray(userSelectedOptions) &&
    userSelectedOptions.length > 0
  ) {
    // User has preferences - use them to filter
    const selectedSet = new Set(userSelectedOptions);
    for (const [key, value] of defaultOptions.entries()) {
      const isSelected = selectedSet.has(key);
      selectedMap.set(key, {
        ...value,
        isFiltered: !isSelected, // Mark as filtered if NOT in selected list
      });
    }
  } else {
    // No preferences - show all options
    for (const [key, value] of defaultOptions.entries()) {
      selectedMap.set(key, {
        ...value,
        isFiltered: false, // Show all by default
      });
    }
  }

  return selectedMap;
};

export const serializeSelectedOptions = (
  selectedOptions: GroupParentAttributeOptionSet,
): string[] => {
  const selectedKeys: string[] = [];

  for (const [key, value] of selectedOptions.entries()) {
    if (!value.isFiltered) {
      selectedKeys.push(key);
    }
  }
  return selectedKeys;
};
